import {skipToken} from '@reduxjs/toolkit/query';
import {FlagProps, Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {getCustomerName, useStateFlag} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';

const LOADING_PLACEHOLDER = '…';

export function useServiceCasePairedRecord(serviceCaseId: string | null): PairedRecordView {
  const {
    data: serviceCase,
    isLoading,
    isError,
  } = useGetMetadaServiceCaseQuery(isNotNil(serviceCaseId) ? {serviceCaseId} : skipToken);

  const stateFlag = useStateFlag(serviceCase?.state);
  const ordersStateFlag = useStateFlag(serviceCase?.ordersState);

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(serviceCase?.customerId) ? {customerId: serviceCase.customerId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const code =
    serviceCase?.number ??
    (isLoading ? LOADING_PLACEHOLDER : i18n.t('general.notifications.notFound'));
  const customerName = getCustomerName(customer);
  const createdAtFormatted = serviceCase?.createdAt
    ? formatDateTime('dateShort', parseDate(serviceCase.createdAt))
    : null;
  const closedAtFormatted = serviceCase?.closedAt
    ? formatDateTime('dateShort', parseDate(serviceCase.closedAt))
    : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(serviceCase?.name), `${i18n.t('general.labels.name')}: ${serviceCase?.name}`)
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(isNotNil(closedAtFormatted), `${i18n.t('general.labels.closed')} ${closedAtFormatted}`);

  const flags = [stateFlag, ordersStateFlag].filter((flag): flag is FlagProps => isNotNil(flag));

  return {
    isLoading,
    isError,
    code,
    detailPath: isNotNil(serviceCaseId)
      ? composePath(workshopRoutes.serviceCaseDetail, {params: {id: serviceCaseId}})
      : null,
    parameters,
    flags,
    vehicleIds: isNotNil(serviceCase?.vehicleId) ? [serviceCase.vehicleId] : [],
  };
}
